import React, { useState, useEffect } from "react";
import { Amplify } from "aws-amplify";
import { signOut, fetchAuthSession } from "@aws-amplify/auth";
import awsconfig from "./aws-exports";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import logoGif from "./Assets/logo - Colored Background.png"; // Ensure your GIF file path is correct
import NavigationBar from "./Nav.jsx"; 
import LandingPage from "./LandingPage.jsx"; // Use your own component
import Support from "./Support.jsx"; // Use your own component
import "./App.css";
import { getAuthConfig } from "./authConfig"; // Import dynamic region-based config

Amplify.configure(awsconfig);

const App = () => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [authConfig, setAuthConfig] = useState(null);

  useEffect(() => {
    const configureAuth = async () => {
      try {
       
        const config = await getAuthConfig();
        setAuthConfig(config);
        Amplify.configure(config);
      
      } catch (error) {
       
      }
    };
    configureAuth();
  }, []);



  useEffect(() => {
    if (!authConfig) return;

    

    const urlParams = new URLSearchParams(window.location.hash.substr(1));
    const idToken = urlParams.get("id_token");
    const accessToken = urlParams.get("access_token");

    if (idToken && accessToken && !isExpired(idToken)) {
     
      sessionStorage.setItem("idToken", idToken);
      sessionStorage.setItem("accessToken", accessToken);
      
      try {
        // Decode user information
        const userData = JSON.parse(atob(idToken.split(".")[1]));
        setUser(userData);
      
      } catch (error) {
       
      }

      // Remove token from URL to prevent looping
      window.history.replaceState({}, document.title, window.location.pathname);
      setIsLoading(false);
    } else {
     
      checkUserSession();
    }
  }, [authConfig]);


  useEffect(() => {
  if (!user || !authConfig) return;

  const token = sessionStorage.getItem("idToken");
  if (!token) return;

  let timerId;

  const enforce = () => {
    if (!isExpired(token)) return false;
    sessionStorage.removeItem("idToken");
    sessionStorage.removeItem("accessToken");
    setUser(null);
    handleSignIn();          // or handleSignOut() to also kill the Cognito session
    return true;
  };

  const schedule = () => {
    clearTimeout(timerId);
    const { exp } = JSON.parse(atob(token.split(".")[1]));
    const msLeft = exp * 1000 - 60000 - Date.now();   // same 60s skew as isExpired
    timerId = setTimeout(enforce, Math.max(msLeft, 1000));
  };

  const onWake = () => {
    if (document.visibilityState === "visible" && !enforce()) schedule();
  };

  if (!enforce()) schedule();
  document.addEventListener("visibilitychange", onWake);
  window.addEventListener("focus", onWake);

  return () => {
    clearTimeout(timerId);
    document.removeEventListener("visibilitychange", onWake);
    window.removeEventListener("focus", onWake);
  };
}, [user, authConfig]);

const isExpired = (token) => {
  try {
    const { exp } = JSON.parse(atob(token.split(".")[1]));
    // 60s skew so a token about to die is treated as already dead
    return !exp || Date.now() >= (exp * 1000) - 60000;
  } catch {
    return true;
  }
};


const checkUserSession = async () => {
  const storedIdToken = sessionStorage.getItem("idToken");

  if (storedIdToken && !isExpired(storedIdToken)) {
    setUser(JSON.parse(atob(storedIdToken.split(".")[1])));
    setIsLoading(false);
    return;
  }

  // Expired or absent: clear it out and send them back through login
  sessionStorage.removeItem("idToken");
  sessionStorage.removeItem("accessToken");

  try {
    const session = await fetchAuthSession();
    if (session?.tokens?.idToken) {
      const jwt = session.tokens.idToken.toString();
      sessionStorage.setItem("idToken", jwt);
      setUser(session.tokens.idToken.payload);
      setIsLoading(false);
      return;
    }
  } catch (err) {
    // fall through to sign-in
  }

  setIsLoading(false);
  handleSignIn();
};

 
const handleSignIn = () => {
  if (!authConfig) return;
  const signInUrl = `${authConfig.oauth.domain}/signup?client_id=${authConfig.aws_user_pools_web_client_id}&response_type=token&scope=email+openid+phone+profile&redirect_uri=${encodeURIComponent(authConfig.oauth.redirectSignIn)}`;
  window.location.href = signInUrl;
};

const POST_LOGOUT_URL = "https://go.srvql.com/";

const handleSignOut = () => {
  if (!authConfig) return;
  signOut().then(() => {
    setUser(null);
    sessionStorage.removeItem("idToken");
    sessionStorage.removeItem("accessToken");
    localStorage.removeItem("idToken");
    localStorage.removeItem("termsAccepted");
    const signOutUrl = `${authConfig.oauth.domain}/logout?client_id=${authConfig.aws_user_pools_web_client_id}&logout_uri=${encodeURIComponent(POST_LOGOUT_URL)}`;
    window.location.href = signOutUrl;
  });
};

  // If still loading, render the animated logo
  if (isLoading) {
    return (
      <div className="loading-screen">
        <img src={logoGif} alt="Loading..." className="logo-animation" />
      </div>
    );
  }

  return (
    <Router>
      {user ? (
        <>
          <NavigationBar signOut={handleSignOut} user={user} userData={user} />
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/support" element={<Support user={user} />} />
          </Routes>
        </>
      ) : (
        <div className="loading-screen">
          <img src={logoGif} alt="Loading..." className="logo-animation" />
        </div>
      )}
    </Router>
  );
};

export default App;
